import { useEffect, useState } from "react";
import apiClient from "../utils/apiClient";
import Categories from "../components/Categories";
import {
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
} from "@mui/material";
import { Add, Search as SearchIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/admin/PageLayout";

const Category = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("category");

      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      showSnackbar("Error fetching categories", "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Function to handle adding a new category
  const handleAddCategory = () => {
    navigate("/add-category");
  };

  const handleToggleActive = async (categoryId, currentStatus) => {
    try {
      const response = await apiClient.post("category/update-status", {
        id: categoryId,
        active: !currentStatus,
      });

      if (response.data.success) {
        setCategories((prevCategories) =>
          prevCategories.map((category) =>
            category.id === categoryId
              ? { ...category, active: !currentStatus }
              : category
          )
        );
        showSnackbar("Category status updated successfully");
      } else {
        showSnackbar("Failed to update category status", "error");
      }
    } catch (error) {
      console.error("Error updating category status:", error);
      showSnackbar("Error updating category status", "error");
    }
  };

  // Function to handle editing a category
  const handleEditCategory = (categoryId) => {
    navigate(`/edit-category/${categoryId}`);
  };

  // Function to handle opening delete confirmation dialog
  const handleDeleteClick = (categoryId) => {
    setCategoryToDelete(categoryId);
    setDeleteDialogOpen(true);
  };

  // Function to handle closing delete confirmation dialog
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  // Function to handle deleting a category
  const handleDeleteCategory = async () => {
    if (!categoryToDelete) {
      console.error("No category selected for deletion");
      return;
    }

    try {
      console.log("Attempting to delete category:", categoryToDelete);

      const response = await apiClient.delete(`category/${categoryToDelete}`);

      console.log("Delete response:", response.data);

      if (response.data.success) {
        setCategories((prevCategories) =>
          prevCategories.filter((category) => category.id !== categoryToDelete)
        );
        showSnackbar("Category deleted successfully");
      } else {
        showSnackbar(
          response.data.message || "Failed to delete category",
          "error"
        );
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      const errorMessage =
        error.response?.data?.message || "Error deleting category";
      showSnackbar(errorMessage, "error");
    } finally {
      handleDeleteDialogClose();
    }
  };

  return (
    <PageLayout
      title="Categories"
      subtitle="Manage income and expense categories. Toggle active or edit and delete."
      action={
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            variant="outlined"
            placeholder="Search…"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              "& .MuiOutlinedInput-root": { bgcolor: "background.paper", borderRadius: 2 },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddCategory}
          >
            Add Category
          </Button>
        </Box>
      }
    >
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={280}
        >
          <CircularProgress size={48} sx={{ color: "primary.main" }} />
        </Box>
      ) : (
        <Categories
          categories={categories}
          onToggleActive={handleToggleActive}
          edit={handleEditCategory}
          deleteItem={handleDeleteClick}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Category</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this category? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteCategory} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default Category;
